import { useEffect, useMemo, useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Search, Eye, Download, RotateCcw, ShieldCheck, X, Calendar, User as UserIcon, BookOpen, Award, Upload, FileCheck2, CheckCircle2, AlertTriangle, LayoutPanelTop, LayoutTemplate, UserPlus, Sparkles } from "lucide-react";

export default function CertificatesAdmin() {
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({ student_name: "", course_id: "", date_from: "", date_to: "", source: "" });
  const [applied, setApplied] = useState({ student_name: "", course_id: "", date_from: "", date_to: "", source: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, counters: { manual: 0, organic: 0 } });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [flash, setFlash] = useState("");
  const [dc3ConfirmFor, setDc3ConfirmFor] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/admin/settings/certificate-template").then((r) => setTemplateInfo(r.data)).catch(() => {});
  }, []);

  const saveTemplate = async (key) => {
    setSavingTemplate(true); setErr("");
    try {
      await api.put("/admin/settings/certificate-template", { template: key });
      const r = await api.get("/admin/settings/certificate-template");
      setTemplateInfo(r.data);
      setFlash(`Plantilla activa: ${key === "vertical_2026" ? "Vertical 2026" : "Horizontal legacy"}`);
      setTimeout(() => setFlash(""), 2800);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingTemplate(false); }
  };

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams({ page, limit, ...applied });
      const r = await api.get(`/admin/certificates?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [applied, page, limit]);

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const showingFrom = data.items.length ? (page - 1) * limit + 1 : 0;
  const showingTo = (page - 1) * limit + data.items.length;

  const applyFilters = () => { setPage(1); setApplied({ ...filters }); };
  const clearFilters = () => {
    const empty = { student_name: "", course_id: "", date_from: "", date_to: "", source: "" };
    setFilters(empty); setApplied(empty); setPage(1);
  };

  const downloadPdf = async (enrollmentId, studentName) => {
    try {
      const resp = await api.get(`/admin/certificates/${enrollmentId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificado-${studentName.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadDc3 = async (enrollmentId, studentName) => {
    try {
      const resp = await api.get(`/admin/certificates/${enrollmentId}/dc3`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DC3-${studentName.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const triggerDc3Upload = (enrollmentId) => {
    // Show confirmation modal first; actual file picker opens after "Continuar"
    const cert = data.items.find((c) => c.enrollment_id === enrollmentId);
    setDc3ConfirmFor(cert || { enrollment_id: enrollmentId });
    setErr("");
  };

  const proceedDc3Upload = () => {
    if (!dc3ConfirmFor) return;
    uploadTargetRef.current = dc3ConfirmFor.enrollment_id;
    setDc3ConfirmFor(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleDc3Selected = async (e) => {
    const file = e.target.files?.[0];
    const enrollmentId = uploadTargetRef.current;
    if (!file || !enrollmentId) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setErr("Solo se permite formato PDF");
      return;
    }
    setUploadingId(enrollmentId); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/certificates/${enrollmentId}/dc3`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFlash("DC-3 cargada correctamente. Se está respaldando en OneDrive.");
      load();
      setTimeout(() => setFlash(""), 3500);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setUploadingId(null);
      uploadTargetRef.current = null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-naranja" />
        <h2 className="font-heading font-bold text-marino text-xl">Certificados emitidos</h2>
      </div>

      {/* Plantilla activa toggle */}
      {templateInfo && (
        <div data-testid="template-selector" className="bg-white border border-gris/30 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-marino" />
              <h3 className="font-heading font-bold text-marino text-sm uppercase tracking-wider">Plantilla de certificado en emisión</h3>
            </div>
            {templateInfo.updated_at && (
              <span className="text-xs text-marino/60">
                Última actualización: {templateInfo.updated_at.slice(0, 10)} · {templateInfo.updated_by || "—"}
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {templateInfo.options.map((opt) => {
              const active = templateInfo.template === opt.key;
              return (
                <button
                  key={opt.key}
                  data-testid={`template-option-${opt.key}`}
                  onClick={() => !active && saveTemplate(opt.key)}
                  disabled={savingTemplate}
                  className={`text-left p-4 rounded-lg border-2 transition-all ${active
                    ? "border-naranja bg-naranja/5"
                    : "border-gris/40 bg-white hover:border-marino/40"}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <LayoutPanelTop className={`w-4 h-4 ${active ? "text-naranja" : "text-marino/60"}`} />
                    <span className={`font-bold text-sm ${active ? "text-naranja" : "text-marino"}`}>{opt.label}</span>
                    {active && <span className="ml-auto text-[10px] uppercase tracking-wider bg-naranja text-white px-2 py-0.5 rounded-full font-bold">activa</span>}
                  </div>
                  <p className="text-xs text-marino/60 leading-relaxed">{opt.description}</p>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-marino/50 mt-3">Todos los certificados descargados a partir de este momento usarán la plantilla seleccionada.</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gris/30 rounded-xl p-5 shadow-sm">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-marino/70 mb-1 font-medium">Estudiante</label>
            <div className="relative">
              <Search className="w-4 h-4 text-gris absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="filter-student"
                value={filters.student_name}
                onChange={(e) => setFilters({ ...filters, student_name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="Nombre del estudiante..."
                className="w-full pl-9 pr-3 py-2 border border-gris rounded-lg text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-marino/70 mb-1 font-medium">Curso</label>
            <select
              data-testid="filter-course"
              value={filters.course_id}
              onChange={(e) => setFilters({ ...filters, course_id: e.target.value })}
              className="w-full px-3 py-2 border border-gris rounded-lg text-sm bg-white"
            >
              <option value="">Todos los cursos</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-marino/70 mb-1 font-medium">Desde</label>
            <input data-testid="filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-marino/70 mb-1 font-medium">Hasta</label>
            <input data-testid="filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="w-full px-3 py-2 border border-gris rounded-lg text-sm" />
          </div>
        </div>
        <div className="mt-3 grid md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-marino/70 mb-1 font-medium">Origen</label>
            <select
              data-testid="filter-source"
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
              className="w-full px-3 py-2 border border-gris rounded-lg text-sm bg-white"
            >
              <option value="">Todos los orígenes</option>
              <option value="organic">Orgánico (curso completado)</option>
              <option value="manual">Registro manual (backfill)</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button data-testid="filter-clear" onClick={clearFilters} className="bg-gris text-marino px-4 py-2 rounded-lg text-sm font-medium hover:bg-grisHover inline-flex items-center gap-1">
            <RotateCcw className="w-4 h-4" /> Limpiar
          </button>
          <button data-testid="filter-apply" onClick={applyFilters} className="bg-naranja text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-naranjaHover inline-flex items-center gap-1">
            <Search className="w-4 h-4" /> Buscar
          </button>
        </div>
      </div>

      {err && <div className="text-naranja text-sm bg-naranja/10 border border-naranja/30 rounded-lg p-3">{err}</div>}
      {flash && <div data-testid="flash-msg" className="text-green-800 text-sm bg-green-50 border border-green-200 rounded-lg p-3 inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {flash}</div>}

      {/* Hidden file input for DC-3 upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleDc3Selected}
        className="hidden"
        data-testid="dc3-file-input"
      />

      {/* Results table */}
      <div className="bg-white border border-gris/30 rounded-xl overflow-hidden shadow-sm">
        {/* Source counters strip */}
        <div data-testid="source-counters" className="grid grid-cols-2 border-b border-gris/20">
          <div className="p-4 border-r border-gris/20">
            <div className="text-[10px] uppercase tracking-wider text-marino/60 font-semibold">Certificados orgánicos</div>
            <div className="text-2xl font-heading font-bold text-marino mt-0.5" data-testid="counter-organic">{data.counters?.organic ?? 0}</div>
            <div className="text-[11px] text-marino/50">Emitidos al completar el curso en la plataforma</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-wider text-naranja font-semibold">Registro manual (backfill)</div>
            <div className="text-2xl font-heading font-bold text-naranja mt-0.5" data-testid="counter-manual">{data.counters?.manual ?? 0}</div>
            <div className="text-[11px] text-marino/50">Cargados por admin para casos históricos</div>
          </div>
        </div>
        <div className="flex items-center justify-between p-4 border-b border-gris/20 text-sm">
          <span className="text-marino/70">
            {loading ? "Buscando..." : (
              data.total > 0
                ? <>Mostrando <strong className="text-marino">{showingFrom}-{showingTo}</strong> de <strong className="text-marino">{data.total}</strong> certificados</>
                : "Sin certificados con estos filtros"
            )}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-marino/60 text-xs">Por página:</span>
            <select data-testid="page-size" value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }} className="border border-gris rounded px-2 py-1 text-xs">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-marino/5 text-marino/80 text-xs uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Folio</th>
                <th className="p-3 text-left">Estudiante</th>
                <th className="p-3 text-left">Curso</th>
                <th className="p-3 text-left">Fecha emisión</th>
                <th className="p-3 text-center">Origen</th>
                <th className="p-3 text-right">Calificación</th>
                <th className="p-3 text-center">DC-3</th>
                <th className="p-3 text-center w-40">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((cert) => (
                <tr key={cert.enrollment_id} data-testid={`cert-row-${cert.enrollment_id}`} className="border-b border-gris/10 hover:bg-marino/[0.02]">
                  <td className="p-3 font-mono text-xs text-marino/70">{cert.enrollment_id.slice(0, 8).toUpperCase()}</td>
                  <td className="p-3">
                    <div className="text-marino font-medium">{cert.student_name}</div>
                    <div className="text-xs text-marino/60">{cert.student_email}</div>
                  </td>
                  <td className="p-3 text-marino">{cert.course_title}</td>
                  <td className="p-3 text-marino/80">{(cert.issued_at || "").slice(0, 10)}</td>
                  <td className="p-3 text-center">
                    {cert.manual_enrollment ? (
                      <span
                        data-testid={`source-badge-${cert.enrollment_id}`}
                        title={cert.manual_enrollment_by ? `Cargado por ${cert.manual_enrollment_by}${cert.manual_enrollment_at ? " · " + cert.manual_enrollment_at.slice(0, 10) : ""}` : "Registro manual"}
                        className="inline-flex items-center gap-1 text-[11px] bg-naranja/15 text-naranja px-2 py-1 rounded-full font-semibold"
                      >
                        <UserPlus className="w-3 h-3" /> Manual
                      </span>
                    ) : (
                      <span
                        data-testid={`source-badge-${cert.enrollment_id}`}
                        title="Certificado emitido tras completar el curso en la plataforma"
                        className="inline-flex items-center gap-1 text-[11px] bg-marino/10 text-marino px-2 py-1 rounded-full font-semibold"
                      >
                        <Sparkles className="w-3 h-3" /> Orgánico
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">{cert.score}%</span>
                  </td>
                  <td className="p-3 text-center">
                    {cert.dc3_uploaded ? (
                      <span data-testid={`dc3-status-${cert.enrollment_id}`} className="inline-flex items-center gap-1 text-[11px] bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Cargado
                      </span>
                    ) : cert.dc3_requested_at ? (
                      <span data-testid={`dc3-status-${cert.enrollment_id}`} className="inline-flex items-center gap-1 text-[11px] bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-semibold">
                        <FileCheck2 className="w-3 h-3" /> Solicitada
                      </span>
                    ) : (
                      <span data-testid={`dc3-status-${cert.enrollment_id}`} className="text-[11px] text-marino/40">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-center gap-1.5 flex-wrap">
                      <button data-testid={`view-cert-${cert.enrollment_id}`} onClick={() => setDetail(cert)} title="Ver detalles" className="p-1.5 bg-marino/5 text-marino hover:bg-marino hover:text-white rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button data-testid={`download-cert-${cert.enrollment_id}`} onClick={() => downloadPdf(cert.enrollment_id, cert.student_name)} title="Descargar certificado PDF" className="p-1.5 bg-naranja/10 text-naranja hover:bg-naranja hover:text-white rounded-lg transition-colors">
                        <Download className="w-4 h-4" />
                      </button>
                      {cert.dc3_uploaded ? (
                        <button
                          data-testid={`download-dc3-${cert.enrollment_id}`}
                          onClick={() => downloadDc3(cert.enrollment_id, cert.student_name)}
                          title="Descargar DC-3 firmada"
                          className="p-1.5 bg-green-100 text-green-700 hover:bg-green-600 hover:text-white rounded-lg transition-colors"
                        >
                          <FileCheck2 className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          data-testid={`upload-dc3-${cert.enrollment_id}`}
                          onClick={() => triggerDc3Upload(cert.enrollment_id)}
                          disabled={uploadingId === cert.enrollment_id}
                          title="Cargar DC-3 firmada (PDF)"
                          className="p-1.5 bg-marino/10 text-marino hover:bg-marino hover:text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {uploadingId === cert.enrollment_id ? (
                            <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-marino/60">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.total > 0 && (
          <div className="flex items-center justify-between p-3 border-t border-gris/20 text-sm">
            <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-marino disabled:text-gris disabled:cursor-not-allowed hover:text-naranja">← Anterior</button>
            <span className="text-marino/70">Página <strong className="text-marino">{page}</strong> de {totalPages}</span>
            <button data-testid="page-next" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="text-marino disabled:text-gris disabled:cursor-not-allowed hover:text-naranja">Siguiente →</button>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div data-testid="cert-detail-modal" className="fixed inset-0 z-[80] bg-marino/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in">
            <div className="bg-marino text-white px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-naranja" />
                <h3 className="font-heading font-bold text-lg">Detalle del certificado</h3>
              </div>
              <button data-testid="cert-detail-close" onClick={() => setDetail(null)} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <Row icon={UserIcon} label="Otorgado a" value={detail.student_name} sub={detail.student_email} />
              <Row icon={BookOpen} label="Curso" value={detail.course_title} sub={detail.course_category} />
              <Row icon={Award} label="Calificación" value={`${detail.score}%`} />
              <Row icon={Calendar} label="Fecha de emisión" value={(detail.issued_at || "").slice(0, 10)} sub={(detail.issued_at || "").slice(11, 19)} />
              <div className="flex items-start gap-3">
                <UserPlus className="w-5 h-5 text-naranja mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-marino/60 font-semibold">Origen</div>
                  {detail.manual_enrollment ? (
                    <>
                      <div className="text-marino font-medium mt-0.5">Registro manual (backfill)</div>
                      {detail.manual_enrollment_by && (
                        <div className="text-xs text-marino/60 mt-0.5">
                          Cargado por <b>{detail.manual_enrollment_by}</b>
                          {detail.manual_enrollment_at ? ` · ${detail.manual_enrollment_at.slice(0, 10)}` : ""}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-marino font-medium mt-0.5">Orgánico — curso completado en plataforma</div>
                  )}
                </div>
              </div>
              <div className="pt-3 border-t border-gris/20">
                <div className="text-xs uppercase tracking-wider text-marino/60 font-semibold">Folio completo</div>
                <div className="mt-1 font-mono text-xs text-marino break-all">{detail.enrollment_id}</div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => downloadPdf(detail.enrollment_id, detail.student_name)} className="flex-1 bg-naranja text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-naranjaHover inline-flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" /> Descargar PDF
                </button>
                <a target="_blank" rel="noreferrer" href={`/verify/${detail.enrollment_id}`} className="bg-gris text-marino px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-grisHover inline-flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Ver verificación
                </a>
              </div>
              {detail.onedrive_cert_url ? (
                <a
                  data-testid="onedrive-link"
                  target="_blank"
                  rel="noreferrer"
                  href={detail.onedrive_cert_url}
                  className="block text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 hover:bg-green-100"
                >
                  ✓ Respaldado en OneDrive — abrir archivo
                </a>
              ) : (
                <div className="text-xs text-marino/60 bg-marino/5 border border-gris/30 rounded-lg px-3 py-2">
                  Respaldo en OneDrive pendiente. Descarga el PDF para forzar el respaldo automático.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DC-3 upload confirmation modal */}
      {dc3ConfirmFor && (
        <div data-testid="dc3-confirm-modal" className="fixed inset-0 z-[80] bg-marino/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in">
            <div className="bg-marino text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-naranja" />
                <h3 className="font-heading font-bold">Confirmar carga DC-3</h3>
              </div>
              <button data-testid="dc3-confirm-close" onClick={() => setDc3ConfirmFor(null)} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-3 bg-naranja/5 border-l-4 border-naranja rounded-lg p-3">
                <AlertTriangle className="w-5 h-5 text-naranja flex-shrink-0 mt-0.5" />
                <p className="text-marino text-sm leading-relaxed">
                  Asegúrate de seleccionar el documento correcto y de que esté firmado antes de continuar, para evitar subir un archivo equivocado.
                </p>
              </div>
              {dc3ConfirmFor.student_name && (
                <div className="bg-marino/5 rounded-lg p-3 text-sm">
                  <div className="text-marino/60 text-xs uppercase tracking-wider font-semibold mb-1">Documento para</div>
                  <div className="font-semibold text-marino">{dc3ConfirmFor.student_name}</div>
                  <div className="text-marino/70 text-xs mt-0.5">{dc3ConfirmFor.course_title}</div>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  data-testid="dc3-confirm-cancel"
                  onClick={() => setDc3ConfirmFor(null)}
                  className="flex-1 bg-gris text-marino px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-grisHover"
                >
                  Cancelar
                </button>
                <button
                  data-testid="dc3-confirm-continue"
                  onClick={proceedDc3Upload}
                  className="flex-1 bg-naranja text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-naranjaHover inline-flex items-center justify-center gap-2"
                >
                  <Upload className="w-4 h-4" /> Continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, sub }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-naranja mt-0.5 flex-shrink-0" strokeWidth={1.75} />
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wider text-marino/60 font-semibold">{label}</div>
        <div className="text-marino font-medium mt-0.5 break-words">{value}</div>
        {sub && <div className="text-xs text-marino/60 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
